import { QueryClient } from "@tanstack/react-query";
import { resetOfflineStorage } from "@stockline/web";
import api from "@/app/api";
import { setSentryUserContext } from "@/lib/observability/sentryTenant";

/**
 * Centralized logout utility that properly clears all auth state.
 *
 * Order matters: resetOfflineStorage runs FIRST so the change queue can be
 * drained while the bearer token is still valid AND the local IndexedDB is
 * wiped before we navigate; only then do we revoke the token, drop the
 * in-memory query cache, and hard-reload to /login. Wipe-then-redirect
 * ordering is mandatory — a navigation that fires before the wipe completes
 * can leave the next user staring at the previous user's offline data.
 *
 * The redirect lives in `finally` so the user is never stranded on the old
 * page when a cleanup step throws. `resetOfflineStorage` caps every async
 * step internally (flush ≤3s, Dexie delete ≤3s, legacy IDB onblocked
 * resolves immediately) so the absolute worst-case wait is ≈6s.
 */
export async function handleLogout(queryClient: QueryClient): Promise<void> {
  const t0 = performance.now();
  const since = () => `${Math.round(performance.now() - t0)}ms`;
  console.log("[handleLogout] start");
  try {
    await resetOfflineStorage({ tryFlush: true });
    console.log(`[handleLogout] ${since()} reset done, revoking token`);
    api.logOut();
    queryClient.clear();
  } catch (error) {
    console.error(`[handleLogout] ${since()} cleanup failed:`, error);
  } finally {
    // Clear Sentry user + tenant scope on the actual logout path. This is a
    // hard navigation (no `user = null` React render happens before the page
    // tears down), so the reactive `useObservabilityContext` effect never
    // fires its clear — any event captured during teardown would otherwise
    // keep the previous tenant's tags (Constitution Article III).
    setSentryUserContext(null);
    console.log(`[handleLogout] ${since()} redirecting to /login`);
    window.location.href = "/login";
  }
}
